import { createTransparentColor } from '../utils';
import { tbColors } from './tbColors';

const text = {
  primary: tbColors.all.ash[900],
  secondary: tbColors.all.ash[500],
  disabled: tbColors.all.ash[300],
  disclaimer: tbColors.all.ash[600],
  placeholder: tbColors.all.ash[400],
  inverse: tbColors.all.snow[50],
  inverseSecondary: tbColors.all.ash[100],
  positive: tbColors.all.ash[900],
  warning: tbColors.all.ash[900],
  critical: tbColors.all.ash[900],
  highlight: tbColors.all.ash[900],
};

const ui = {
  default: tbColors.all.ash[900],
  secondary: tbColors.all.ash[600],
  disable: tbColors.all.ash[300],
  inverse: tbColors.all.snow[50],
  inverseSecondary: tbColors.all.snow[600],
  navText: tbColors.all.ash[900],
  navTextHover: tbColors.all.ash[700],
  navIconHover: tbColors.all.ash[700],
  navIconActive: tbColors.all.turquoise[500],
  positive: tbColors.all.laurel[500],
  positiveLight: tbColors.all.laurel[200],
  warning: tbColors.all.sand[500],
  warningLight: tbColors.all.sand[200],
  critical: tbColors.all.coral[500],
  criticalLight: tbColors.all.coral[200],
  highlight: tbColors.all.denim[500],
  highlightLight: tbColors.all.denim[200],
  content: tbColors.all.snow[50],
  navIcon: '#868686',
};

const border = {
  default: tbColors.all.snow[200],
  secondary: tbColors.all.snow[400],
  tertiary: tbColors.all.snow[500],
  inverse: tbColors.all.snow[50],
};

const surface = {
  default: tbColors.all.grey[50],
  inverse: tbColors.all.ash[900],
  grey: tbColors.all.snow[200],
  positive: tbColors.all.laurel[200],
  warning: tbColors.all.sand[200],
  critical: tbColors.all.coral[200],
  highlight: tbColors.all.denim[200],
};

const background = {
  default: tbColors.all.snow[200],
  dark: tbColors.all.ash[900],
  bright: tbColors.all.snow[50],
  positive: tbColors.all.laurel[50],
  critical: tbColors.all.coral[50],
  warning: tbColors.all.sand[50],
  highlight: tbColors.all.denim[50],
};

const transparent = {
  100: createTransparentColor(tbColors.all.snow[50], 0.05), // 5% opacity
  200: createTransparentColor(tbColors.all.snow[50], 0.1), // 10% opacity
  300: createTransparentColor(tbColors.all.snow[50], 0.2), // 20% opacity
  400: createTransparentColor(tbColors.all.snow[50], 0.5), // 50% opacity
  500: createTransparentColor(tbColors.all.ash[900], 0.05), // 5% opacity
  600: createTransparentColor(tbColors.all.ash[900], 0.1), // 10% opacity
  700: createTransparentColor(tbColors.all.ash[900], 0.2), // 20% opacity
  800: createTransparentColor(tbColors.all.ash[900], 0.5), // 50% opacity
};

const interactive = {
  ui: {
    default: tbColors.all.ash[900],
    hover: tbColors.all.ash[600],
    active: tbColors.all.ash[700],
    focus: tbColors.all.sharp[900],
    disable: tbColors.all.snow[400],
    inverse: tbColors.all.snow[50],
    critical: {
      default: tbColors.all.coral[500],
      hover: tbColors.all.coral[600],
      active: tbColors.all.coral[700],
      disable: tbColors.all.coral[300],
    },
  },
  surface: {
    default: tbColors.all.snow[50],
    hover: tbColors.all.snow[200],
    active: tbColors.all.snow[100],
    disable: tbColors.all.snow[400],
    indicator: tbColors.all.ash[900],
  },
  selected: {
    default: tbColors.all.snow[50],
    hover: tbColors.all.snow[100],
    active: tbColors.all.snow[200],
    nav: {
      default: '#E2F1F2',
      hover: '#E7F8F9',
    },
  },
  border: {
    default: tbColors.all.snow[500],
    hover: tbColors.all.snow[600],
    active: tbColors.all.snow[700],
    prominent: tbColors.all.snow[800],
    disable: tbColors.all.snow[300],
  },
};

const data = {
  vis: {
    default: tbColors.all.turquoise[500],
    comparison: tbColors.all.snow[700],
    warning: tbColors.all.orange[500],
    positive: tbColors.all.laurel[500],
    critical: tbColors.all.coral[500],
    grid: tbColors.all.snow[500],
    baseline: tbColors.all.ash[900],
    axisText: tbColors.all.ash[900],
  },
  qualitative: {
    default1: tbColors.all.darkBlue[100],
    default2: tbColors.all.darkBlue[300],
    default3: tbColors.all.darkBlue[500],
    default4: tbColors.all.darkBlue[700],
    others: tbColors.all.darkBlue[900],
    comparison1: tbColors.all.coolGrey[100],
    comparison2: tbColors.all.coolGrey[300],
    comparison3: tbColors.all.coolGrey[500],
    comparison4: tbColors.all.coolGrey[700],
    positive1: tbColors.all.turquoise[100],
    positive2: tbColors.all.turquoise[300],
    positive3: tbColors.all.turquoise[500],
    positive4: tbColors.all.turquoise[700],
    warning1: tbColors.all.orange[50],
    warning2: tbColors.all.orange[100],
    warning3: tbColors.all.orange[300],
    warning4: tbColors.all.orange[600],
    critical1: tbColors.all.rose[100],
    critical2: tbColors.all.rose[300],
    critical3: tbColors.all.rose[500],
    critical4: tbColors.all.rose[700],
  },
};

export const tbPalette = {
  text,
  ui,
  border,
  surface,
  background,
  transparent,
  interactive,
  data,
} as const;

export type TbPaletteSection = keyof typeof tbPalette;
export type TbTextColors = keyof typeof tbPalette.text;
export type TbUiColors = keyof typeof tbPalette.ui;
export type TbBorderColors = keyof typeof tbPalette.border;
export type TbSurfaceColors = keyof typeof tbPalette.surface;
export type TbBackgroundColors = keyof typeof tbPalette.background;
export type TbTransparentColors = keyof typeof tbPalette.transparent;
export type TbInteractiveColors = keyof typeof tbPalette.interactive;
export type TbDataVisualizationColors = keyof typeof tbPalette.data;
